import React from "react";
import { Link } from "react-router-dom";
import { fieldLabelClasses } from "../../../components/ui";
import { ROUTES } from "../../../routes/paths";

export interface AppointmentAddressNoteProps {
  /** The line to show, or `null` where there is none to show. */
  address: string | null;
  /**
   * Whether that line is the practice's own rather than this appointment's.
   *
   * It only decides the badge, and the badge is the whole point: "Default"
   * says the address is a standing fact about the practice that this dialog
   * is quoting, and its absence says the address belongs to this appointment.
   * Marking an appointment's own address as the default would tell somebody
   * that changing it under My brand moves this sitting, which it does not.
   */
  isDefault: boolean;
  /**
   * Run when the My brand link is followed, so the dialog gets out of the way.
   * Only reachable where there is no address at all.
   */
  onLeave: () => void;
}

/**
 * Where an in-person appointment happens, stated rather than asked for.
 *
 * Both dialogs that deal with an in-person appointment draw this, and neither
 * has a box to type into. An appointment happens at the practice, the
 * practice's address is already on record, and a form that asks for it again
 * per appointment is a form collecting the same answer over and over so that
 * it can go stale in a dozen places at once. It is changed where it is kept.
 *
 * Which is also why the empty case is a way to go and set one rather than a
 * box with a red outline. "Your practice has no address yet" is a thing only
 * this screen can say and only My brand can fix, and a field here could have
 * said neither.
 *
 * A link and not a button, because it goes somewhere — and it leaves the
 * dialog, so whatever was being booked or moved is done afterwards rather
 * than half done. See `onLeave`.
 */
const AppointmentAddressNote: React.FC<AppointmentAddressNoteProps> = ({
  address,
  isDefault,
  onLeave,
}) => (
  <div>
    <div className="flex items-center gap-2">
      <span className={fieldLabelClasses}>Address</span>
      {/* Said out loud, because the address is not asked for: an address
          simply standing under the form would otherwise read as something
          somebody typed rather than as where this is. */}
      {!!address && isDefault && (
        <span className="rounded-full bg-[#EFF0F3] px-2 py-0.5 text-[11px] font-medium text-ink-muted">
          Default
        </span>
      )}
    </div>
    {address ? (
      <p className="mt-1 text-[14px] 3xl:text-[15px] leading-snug text-ink">
        {address}
      </p>
    ) : (
      <p className="mt-1 text-[13px] 3xl:text-[14px] leading-snug text-ink-muted">
        Your practice has no address yet. Add one under{" "}
        <Link
          to={ROUTES.myBrand}
          onClick={onLeave}
          className="font-medium text-brand-dark underline underline-offset-2"
        >
          My brand
        </Link>{" "}
        so clients know where to come.
      </p>
    )}
  </div>
);

export default AppointmentAddressNote;
